import React from "react";
import { IntlShape } from "react-intl";

export const useFilterFormattedString = (intl: IntlShape) => {
  return React.useMemo(() => ({
    analyticsFilterAllLabel: intl.formatMessage({
      defaultMessage: "All",
      description: "Label that applies to most filters to indicate that there should be no value applied in that filter (which means include all values)",
    }),
    analyticsFilterAreaUnitedStatesLabel: intl.formatMessage({
      defaultMessage: "United States",
      description: "Label for the Area radio group that allows selecting a area filter to include only United States usage",
    }),
    analyticsFilterAreaWesternEuropeLabel: intl.formatMessage({
      defaultMessage: "Western Europe",
      description: "Label for the Area radio group that allows selecting a area filter to include only Western Europe usage",
    }),
    analyticsFilterAreaLatinAmericaLabel: intl.formatMessage({
      defaultMessage: "Latin America",
      description: "Label for the Area radio group that allows selecting a area filter to include only Latin America usage",
    }),
    analyticsFilterAreaJapanLabel: intl.formatMessage({
      defaultMessage: "Japan",
      description: "Label for the Area radio group that allows selecting a area filter to include only Japan usage",
    }),
    analyticsFilterAreaIndiaLabel: intl.formatMessage({
      defaultMessage: "India",
      description: "Label for the Area radio group that allows selecting a area filter to include only India usage",
    }),
    analyticsFilterAreaCanadaLabel: intl.formatMessage({
      defaultMessage: "Canada",
      description: "Label for the Area radio group that allows selecting a area filter to include only Canada usage",
    }),
    analyticsFilterAreaAustraliaLabel: intl.formatMessage({
      defaultMessage: "Australia and New Zealand",
      description: "Label for the Area radio group that allows selecting a area filter to include only Australia and New Zealand usage",
    }),
    analyticsFilterAreaUnitedKingdomLabel: intl.formatMessage({
      defaultMessage: "United Kingdom",
      description: "Label for the Area radio group that allows selecting a area filter to include only United Kingdom usage",
    }),
    analyticsFilterAreaFranceLabel: intl.formatMessage({
      defaultMessage: "France",
      description: "Label for the Area radio group that allows selecting a area filter to include only France usage",
    }),
    analyticsFilterAreaAsiaPacificLabel: intl.formatMessage({
      defaultMessage: "Asia Pacific",
      description: "Label for the Area radio group that allows selecting a area filter to include only Asia Pacific usage",
    }),
    analyticsFilterAreaGermanyLabel: intl.formatMessage({
      defaultMessage: "Germany",
      description: "Label for the Area radio group that allows selecting a area filter to include only Germany usage",
    }),
    analyticsFilterAreaSwitzerlandLabel: intl.formatMessage({
      defaultMessage: "Switzerland",
      description: "Label for the Area radio group that allows selecting a area filter to include only Switzerland usage",
    }),
    analyticsFilterAreaKoreaLabel: intl.formatMessage({
      defaultMessage: "Korea",
      description: "Label for the Area radio group that allows selecting a area filter to include only Korea usage",
    }),
    analyticsFilterAreaNetherlandsLabel: intl.formatMessage({
      defaultMessage: "Netherlands",
      description: "Label for the Area radio group that allows selecting a area filter to include only Netherlands usage",
    }),
    analyticsFilterAreaCentralAndEasternEuropeLabel: intl.formatMessage({
      defaultMessage: "Central and Eastern Europe",
      description: "Label for the Area radio group that allows selecting a area filter to include only Central and Eastern Europe usage",
    }),
    analyticsFilterAggTypeUserLabel: intl.formatMessage({
      defaultMessage: "User",
      description: "Label for the Aggregation Type radio group that allows selecting user level aggregation",
    }),
    analyticsFilterAggTypeTenantLabel: intl.formatMessage({
      defaultMessage: "Tenant",
      description: "Label for the Aggregation Type radio group that allows selecting tenant level aggregation",
    }),
    analyticsFilterPlatformDesktopLabel: intl.formatMessage({
      defaultMessage: "Desktop",
      description: "Label for the platform radio group that allows selecting a platform filter to include only Desktop usage",
    }),
    analyticsFilterPlatformMobileLabel: intl.formatMessage({
      defaultMessage: "Mobile",
      description: "Label for the platform radio group that allows selecting a platform filter to include only Mobile usage",
    }),
    analyticsFilterPlatformWebLabel: intl.formatMessage({
      defaultMessage: "Web",
      description: "Label for the platform radio group that allows selecting a platform filter to include only Web usage",
    }),
    analyticsFilterPlatformUniversalLabel: intl.formatMessage({
      defaultMessage: "Universal",
      description: "Label for the platform radio group that allows selecting a platform filter to include only universal usage",
    }),
    analyticsFilterPlatformUnknownLabel: intl.formatMessage({
      defaultMessage: "Unknown",
      description: "Label for the platform radio group that allows selecting a platform filter to include only unknown usage",
    }),
    analyticsFilterOperatingSystemWindowsLabel: intl.formatMessage({
      defaultMessage: "Windows",
      description: "Label for the OS radio group that allows selecting a OS filter to include only Windows usage",
    }),
    analyticsFilterOperatingSystemMacLabel: intl.formatMessage({
      defaultMessage: "Mac",
      description: "Label for the OS radio group that allows selecting a OS filter to include only Mac usage",
    }),
    analyticsFilterOperatingSystemiOSLabel: intl.formatMessage({
      defaultMessage: "iOS",
      description: "Label for the OS radio group that allows selecting a OS filter to include only iOS usage",
    }),
    analyticsFilterOperatingSystemAndroidLabel: intl.formatMessage({
      defaultMessage: "Android",
      description: "Label for the OS radio group that allows selecting a OS filter to include only Android usage",
    }),
    analyticsFilterOperatingSystemChromeOSLabel: intl.formatMessage({
      defaultMessage: "Chrome OS",
      description: "Label for the OS radio group that allows selecting a OS filter to include only Chrome OS usage",
    }),
    analyticsFilterOtherLabel: intl.formatMessage({
      defaultMessage: "Other",
      description: "Label that applies to other values in the filter",
    }),
    analyticsFilterUnknownLabel: intl.formatMessage({
      defaultMessage: "Unknown",
      description: "Label that applies to unknown values in the filter",
    }),

    // Capability translation keys
    analyticsFilterCapabilityMessageExtensionLabel: intl.formatMessage({
      defaultMessage: "Message Extension",
      description: "Translation key for message extension label",
    }),
    analyticsFilterCapabilityPersonalTabPersonalBotLabel: intl.formatMessage({
      defaultMessage: "Personal App",
      description: "Translation key for personal tab and personal bot label",
    }),
    analyticsFilterCapabilityNonPersonalTabLabel: intl.formatMessage({
      defaultMessage: "Tabs",
      description: "Translation key for non-personal tab label",
    }),
    analyticsFilterCapabilityConnectorsLabel: intl.formatMessage({
      defaultMessage: "Connectors",
      description: "Translation key for connectors label",
    }),

    analyticsFilterCapabilityNonPersonalBotLabel: intl.formatMessage({
      defaultMessage: "Bot",
      description: "Translation key for non-personal bot label",
    }),
    analyticsFilterCapabilityPersonalTabLabel: intl.formatMessage({
      defaultMessage: "Personal Tab",
      description: "Translation key for personal tab label",
    }),
    // Host translation keys
    analyticsFilterHostTeamsLabel: intl.formatMessage({
      defaultMessage: "Teams",
      description: "Teams filter label for the analytics page.",
    }),
    // Metric translation keys
    analyticsFilterMetricNewUsersLabel: intl.formatMessage({
      defaultMessage: "New users",
      description: "New users metric label",
    }),
    analyticsFilterMetricReturningUsersLabel: intl.formatMessage({
      defaultMessage: "Returning users",
      description: "Returning users metric label",
    }),
    analyticsFilterMetricResurrectedUsersLabel: intl.formatMessage({
      defaultMessage: "Resurrected users",
      description: "Resurrected users metric label",
    }),

    analyticsFilterMetricLapsedUsersLabel: intl.formatMessage({
      defaultMessage: "Lapsed users",
      description: "Lapsed users metric label",
    }),
    analyticsFilterMetricNewTenantsLabel: intl.formatMessage({
      defaultMessage: "New tenants",
      description: "New tenants metric label",
    }),
    analyticsFilterMetricReturningTenantsLabel: intl.formatMessage({
      defaultMessage: "Returning tenants",
      description: "Returning tenants metric label",
    }),
    analyticsFilterMetricResurrectedTenantsLabel: intl.formatMessage({
      defaultMessage: "Resurrected tenants",
      description: "Resurrected tenants metric label",
    }),
    analyticsFilterMetricLapsedTenantsLabel: intl.formatMessage({
      defaultMessage: "Lapsed tenants",
      description: "Lapsed tenants metric label",
    }),
  }), [intl]);
};

export const getLocalisedFilterContent = (
  intl: IntlShape,
  key: string,
  formattedStrings: ReturnType<typeof useFilterFormattedString>,
) => {
  return key in formattedStrings
    ? formattedStrings[key as keyof typeof formattedStrings]
    : intl.formatMessage({ id: key, defaultMessage: key });
};
